import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getMember, updateMember } from 'services/core/services';
import { MemberData as GetMemberData } from '@smiles/core-account/dist/Types/GetMember.types';
import { UpdateMemberServiceRequestType } from '@smiles/core-account/dist/Types/UpdateMember.types';
import { AcceptTermsType, AddressType } from '@smiles/core-account/dist/Types/CommonTypes';
import { parse } from 'query-string';
import { Config } from '../../../services/core/config';
import { FirstAccessHelpers } from '../helpers/member';

interface CellPhone {
  DDI: string;
  DDD: string;
  number: string;
  formatted: {
    display: string;
  };
}

interface MemberData {
  email: string;
  confirmEmail: string;
  status: string;
  firstName: string;
  terms: boolean;
  document: {
    type: string;
    value: string;
  };
  streetName: string;
  streetNumber: string;
  zipCode: string;
  city: string;
  state: string;
  country: string;
  complement: string;
  cellPhone: CellPhone;
  isForeign: boolean;
}

interface MemberHandlerData {
  email?: string;
  confirmEmail?: string;
  status?: string;
  terms?: boolean;
  document?: {
    type?: string;
    value?: string;
  };
  streetName?: string;
  streetNumber?: string;
  zipCode?: string;
  city?: string;
  state?: string;
  country?: string;
  complement?: string;
  cellPhone?: {
    DDI?: string;
    DDD?: string;
    number?: string;
    formatted?: {
      display?: string;
    };
  };
}

interface MemberDataProps {
  data: MemberData;
  getMemberData: GetMemberData;
  formData: MemberData;
  response: {
    success: boolean;
    ready: boolean;
  };
  formType: 'SIMPLE' | 'COMPLETE' | '';
  setFormType: (value: 'SIMPLE' | 'COMPLETE') => void;
  formEdited: boolean;
  setFormEdit: () => void;
  handler: (state: MemberHandlerData) => void;
  submitMember: () => Promise<void>;
  submittingMember: boolean;
}

const INITIAL_DATA = {
  email: '',
  confirmEmail: '',
  status: '',
  firstName: '',
  terms: false,
  isForeign: false,
  document: {
    type: '',
    value: '',
  },
  streetName: '',
  streetNumber: '',
  zipCode: '',
  city: '',
  state: '',
  country: '',
  complement: '',
  cellPhone: {
    DDI: '',
    DDD: '',
    number: '',
    formatted: {
      display: '',
    },
  },
};

const MemberDataContext = createContext<MemberDataProps>({} as MemberDataProps);

export const useMemberData = () => useContext(MemberDataContext);

export const MemberDataProvider: React.FC = ({ children }) => {
  const TOKEN_NAME = process.env.REACT_APP_TOKEN_NAME || 'session-token';

  const [memberData, setMemberData] = useState<MemberData>(INITIAL_DATA);
  const [formData, setFormData] = useState<MemberData>(INITIAL_DATA);
  const [getMemberData, setGetMemberData] = useState<GetMemberData>({} as GetMemberData);

  const [response, setResponse] = useState({ success: false, ready: false });
  const [currentFormType, setCurrentFormType] = useState<'SIMPLE' | 'COMPLETE' | ''>('');
  const [formEdited, setFormEdited] = useState<boolean>(false);
  const [termsOpened, setTermsOpened] = useState<boolean>(false);

  const [submittingMember, setSubmittingMember] = useState<boolean>(false);

  const params = useMemo(() => parse(window.location.search), [window.location]);

  useEffect(() => {
    const callGetMember = async () => {
      const authToken = localStorage.getItem(TOKEN_NAME);
      if (!authToken) {
        console.log('[CONTEXT] getMemberData: missing auth token');
        return;
      }

      Config.saveTokenAuthUser({ accessToken: authToken });

      try {
        const res = await getMember();

        if (!res.success) {
          const errorMessage = '[CONTEXT][ERROR] getMemberData: request failed';

          console.log(errorMessage);
          throw new Error(errorMessage);
        }

        setGetMemberData(res.data);

        const responseData = res.data;

        const emptyMail = responseData.email === 'mudar@mudar.com.br' ? '' : responseData.email;

        const formattedData: MemberData = {
          email: emptyMail,
          confirmEmail: emptyMail,
          status: responseData.status,
          firstName: responseData.firstName,
          terms: true,
          isForeign: responseData.document.type !== 'CPF',
          document: {
            type: responseData.document.type,
            value: responseData.document.value,
          },
          streetName: responseData.address?.streetName,
          streetNumber: responseData.address?.streetNumber,
          zipCode: responseData.address?.zipCode,
          city: responseData.address?.city,
          state: responseData.address?.state,
          country: responseData.address?.country || '',
          complement: responseData.address?.complement || '',
          cellPhone: {
            DDI: '',
            DDD: '',
            number: '',
            formatted: {
              display: '',
            },
          },
        };

        setMemberData(formattedData);
        setFormData(formattedData);
        setResponse(() => ({ success: true, ready: true }));

        console.log('[CONTEXT][GET MEMBER]', res.data);
      } catch (err) {
        console.log('[GET MEMBER ERROR]', err);
      }
    };

    callGetMember();
  }, []);

  const handler = useCallback(
    (state: MemberHandlerData) => {
      setFormData((prev) => ({ ...prev, ...state } as MemberData));
    },
    [formData],
  );

  const setFormType = (type: 'SIMPLE' | 'COMPLETE') => {
    setCurrentFormType(type);
  };

  const setFormEdit = () => {
    setFormEdited(true);
  };

  async function submitMember() {
    setSubmittingMember(true);

    const memberAddress: AddressType = {
      streetName: formData.streetName,
      streetNumber: formData.streetNumber,
      zipCode: formData.zipCode,
      city: formData.city,
      state: formData.state,
      country: formData.country,
      complement: formData.complement,
    };

    const regularData = {
      address: memberAddress,
      email: formData.email,
      cellPhone: formData.cellPhone,
    };

    let acceptTerms: AcceptTermsType | undefined = undefined;
    if (getMemberData.acceptedGOLRegister !== "Y") {
      acceptTerms = {
        accepted: true,
        termsOpened: false,
      };
    }

    const updateMemberRequest: UpdateMemberServiceRequestType = {
      contactType: 'EMAIL',
      regularData: {
        ...regularData,
        acceptTerms,
      },
    };

    try {
      const updateResponse = await updateMember(updateMemberRequest);

      if (!updateResponse.success) {
        console.log('[updateMember] response not successfull');
      }

      FirstAccessHelpers.ClearToken();

      window.location.href = '/validar/recadastramento/sucesso';
    } catch (e) {
      console.log('[updateMember] error', e);
      FirstAccessHelpers.Logout();
    } finally {
      setSubmittingMember(false);
    }
  }

  return (
    <MemberDataContext.Provider
      value={{
        data: memberData,
        formData,
        response,
        handler,
        formType: currentFormType,
        setFormType,
        setFormEdit,
        formEdited,
        getMemberData,
        submitMember,
        submittingMember,
      }}
    >
      {children}
    </MemberDataContext.Provider>
  );
};
